import { Link } from "wouter";

const NORFOLK_AI_LOGO =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663270229297/i88gywhNYafBWiKD3FZzYw/brand/norfolk-ai-logo_24f57943.png";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border/40 bg-background/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold tracking-tight text-foreground">
              Lola &amp; Ber · Redefining Normal™
            </span>
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <Link href="/terms" className="hover:text-foreground transition-colors">
              Terms of Service
            </Link>
            <Link href="/" className="hover:text-foreground transition-colors">
              ← Back to app
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-10">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
            Legal
          </p>
          <h1 className="text-4xl font-bold tracking-tight mb-3">Privacy Policy</h1>
          <p className="text-muted-foreground text-sm">
            Effective date: <strong>July 1, 2026</strong> &nbsp;·&nbsp; Last updated:{" "}
            <strong>July 12, 2026</strong>
          </p>
        </div>

        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8 text-[15px] leading-relaxed">

          <section>
            <h2 className="text-xl font-semibold mb-3">1. Who We Are</h2>
            <p>
              Lola &amp; Ber ("we", "us", or "our") is a research and data platform operated by{" "}
              <strong>Norfolk Group LLC</strong>, doing business as Norfolk AI. Our platform,
              accessible at{" "}
              <a href="https://lolandberretreats.com/" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-4">lolandberretreats.com</a>,
              provides data research, analysis, and AI-assisted insights about evolving relationship norms and social demographics.
            </p>
            <p className="mt-3">
              Questions about this policy may be directed to:{" "}
              <a href="https://lolandberretreats.com/contact/" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-4">
                Contact Lola & Ber
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">2. Information We Collect</h2>
            <p>We collect the following categories of information:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>
                <strong>Account information</strong> — your name, email address, and role when you
                register or are invited to the platform.
              </li>
              <li>
                <strong>Authentication data</strong> — hashed passwords and one-time magic codes
                used to verify your identity. We never store passwords in plain text.
              </li>
              <li>
                <strong>Usage data</strong> — pages visited, features used, and interactions with
                the Sage AI assistant, stored to improve your experience and our service.
              </li>
              <li>
                <strong>Session data</strong> — a signed session token stored in a secure,
                HTTP-only cookie to keep you logged in.
              </li>
              <li>
                <strong>Sponsor code</strong> — if you were referred by a partner or sponsor, their
                code may be associated with your account.
              </li>
            </ul>
            <p className="mt-3">
              We do <strong>not</strong> collect payment information, government IDs, or sensitive
              personal data beyond what is listed above.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Authenticate you and maintain your session securely.</li>
              <li>Deliver the platform's research, data, and AI features to you.</li>
              <li>Send you transactional emails such as magic sign-in codes and welcome messages.</li>
              <li>Administer user accounts and access controls.</li>
              <li>Monitor platform health, diagnose errors, and improve performance.</li>
              <li>Comply with legal obligations.</li>
            </ul>
            <p className="mt-3">
              We do <strong>not</strong> sell, rent, or trade your personal information to third
              parties for marketing purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">4. Cookies and Tracking</h2>
            <p>
              We use a single first-party HTTP-only session cookie (<code>app_session_id</code>) to
              maintain your authenticated session. This cookie is strictly necessary for the
              platform to function and does not track you across other websites.
            </p>
            <p className="mt-3">
              We do not use third-party advertising cookies, cross-site tracking pixels, or
              behavioral profiling technologies.
            </p>
            <p className="mt-3">
              We use a privacy-respecting analytics service to measure aggregate platform usage
              (page views, session counts). This service does not use cookies and does not collect
              personally identifiable information.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">5. Data Storage and Security</h2>
            <p>
              Your data is stored in a secured cloud database hosted on{" "}
              <strong>Neon (PostgreSQL)</strong> and file assets are stored on{" "}
              <strong>Amazon S3</strong>. Both services are located in the United States. We apply
              industry-standard security measures including encrypted connections (TLS), hashed
              credentials, and role-based access controls.
            </p>
            <p className="mt-3">
              No method of transmission over the internet or electronic storage is 100% secure.
              While we strive to protect your data, we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">6. Third-Party Services</h2>
            <p>We use the following third-party services to operate the platform:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>
                <strong>Resend</strong> — for transactional email delivery (magic codes, welcome
                emails). Resend processes your email address to deliver messages on our behalf.
              </li>
              <li>
                <strong>Neon</strong> — cloud PostgreSQL database hosting.
              </li>
              <li>
                <strong>Amazon Web Services (S3 / CloudFront)</strong> — file storage and CDN
                delivery.
              </li>
              <li>
                <strong>Pinecone</strong> — vector database for AI-powered search features.
              </li>
              <li>
                <strong>Google Gemini / Anthropic Claude</strong> — AI language models that power
                the Sage assistant. Queries you send to Sage may be processed by these providers.
                We do not share your account information with them.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">7. Data Retention</h2>
            <p>
              We retain your account information for as long as your account is active. If you
              request deletion of your account, we will remove your personal data within 30 days,
              except where retention is required by law or legitimate business necessity (e.g.,
              audit logs).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">8. Your Rights</h2>
            <p>
              Depending on your jurisdiction, you may have the right to access, correct, or delete
              your personal data, or to object to or restrict certain processing. To exercise any
              of these rights, please contact us at{" "}
              <a href="https://lolandberretreats.com/contact/" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-4">
                Contact Lola & Ber
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">9. Children's Privacy</h2>
            <p>
              This platform is not directed at children under the age of 18. We do not knowingly
              collect personal information from minors. If you believe a minor has provided us with
              personal information, please contact us and we will delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we do, we will revise the
              "Last updated" date at the top of this page. Continued use of the platform after
              changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">11. Contact Us</h2>
            <p>
              If you have questions or concerns about this Privacy Policy, please contact us at:
            </p>
            <address className="not-italic mt-3 text-muted-foreground">
              <strong className="text-foreground">Norfolk Group LLC</strong>
              <br />
              Email:{" "}
              <a href="https://lolandberretreats.com/contact/" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-4">
                Contact Lola & Ber
              </a>
            </address>
          </section>
        </div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-border/40 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <span>© 2026 Norfolk Group LLC · Lola &amp; Ber · Redefining Normal™</span>
          <div className="flex items-center gap-4">
            <Link href="/terms" className="hover:text-foreground transition-colors">
              Terms of Service
            </Link>
            <span>·</span>
            <Link href="/" className="hover:text-foreground transition-colors">
              Back to app
            </Link>
          </div>
        </footer>

        {/* Powered by Norfolk AI */}
        <div className="mt-8 flex items-center justify-center gap-2 opacity-50">
          <img src={NORFOLK_AI_LOGO} alt="Norfolk AI" className="h-4 w-4 object-contain" />
          <span className="text-xs text-muted-foreground">
            Powered by <span className="font-semibold">Norfolk AI</span>
          </span>
        </div>
      </main>
    </div>
  );
}
